import { useCallback } from 'react';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { useExportAllImages } from '../../export';
import { useVideoExport } from '../../media';
import { usePlayback } from '../../playback';
import { CanvasJSON } from '../../canvas/types';
import { ResolutionConfig } from '../../shared/types/binding-types';
import { usePieceSelector } from '../../model/piece-context';
import type { PieceModel } from '../../model/types';
import { useModelComposition } from '../../model/use-model-composition';
import { hasTemporalContent } from '../../model/selectors';

type UseCreativityEditorOptions = {
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>;
  getModel: () => PieceModel;
  getExportFileName?: (resolution: ResolutionConfig) => string;
  onExportComplete?: (blob: Blob) => void;
};

const defaultExportFileName = (resolution: ResolutionConfig) => `export-${resolution.id}.png`;

/**
 * Sets up the main editor features, including the timeline and exports.
 * App-specific behavior such as saving and navigation stays outside this hook.
 */
export const useCreativityEditor = ({
  resolveResolutionJson,
  getModel,
  getExportFileName = defaultExportFileName,
  onExportComplete
}: UseCreativityEditorOptions) => {
  const { canvas, transaction } = useEditorCanvas();
  const { resolutions, activeResolution, setActiveResolutionId } = useResolutions();
  const { pause } = usePlayback();

  const composition = useModelComposition();

  const {
    exportVideo,
    recordVideo,
    cancelVideoExport,
    isExportingVideo,
    progress,
    recordingMode,
    exportNotice,
    dismissExportNotice
  } = useVideoExport({ getModel });

  const { exportAll, isExportingAll } = useExportAllImages({ resolutions, resolveResolutionJson });

  // Checks whether the piece includes audio or animations.
  const hasTimelineContent = usePieceSelector(hasTemporalContent);

  // Shows the timeline only when the piece has time-based content.
  const showTimeline = hasTimelineContent;

  const handleExport = useCallback(() => {
    pause(); // the preview would keep playing on its own otherwise
    if (hasTimelineContent) {
      exportVideo(onExportComplete);
      return;
    }
    exportAll({ getFileName: getExportFileName });
  }, [pause, hasTimelineContent, exportVideo, exportAll, getExportFileName, onExportComplete]);

  return {
    canvas,
    transaction,
    composition,
    resolutions,
    activeResolution,
    setActiveResolutionId,
    hasTimelineContent,
    showTimeline,
    handleExport,
    recordVideo,
    isExportingVideo,
    isExportingAll,
    progress,
    recordingMode,
    cancelVideoExport,
    exportNotice,
    dismissExportNotice
  };
};
